import React, { useState, Fragment, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { setSendersEmail, sendSmsToSender } from './senderSlice';
import CountrySelect from './CountrySelect';
import PhoneNumberInput from './PhoneNumberInput';
import ReCaptcha from './ReCaptcha';

import Card from '../../components/Card';
import EmailInput from './EmailInput';
import BaseButton from '../../components/BaseButton';

import { isValidEmail, isValidPhoneNumber } from '../../lib/utils';
import { getCountryEmojiByCountryCode } from '../../lib/countries';

import styles from './ReceiptDetails.module.scss';
import { steps, setSingleStep } from '../step/stepSlice';

export default function ReceiptDetails() {
  const { email: emailFromState } = useSelector((state) => state.sender);
  const { step } = useSelector((state) => state.step);
  const { fullPhoneNumber } = useSelector((state) => state.sender);
  const dispatch = useDispatch();

  const [email, setEmail] = useState(emailFromState || '');
  const [error, setError] = useState({ email: false, phoneNumber: false, reCaptcha: false });
  const [reCaptchaToken, setRecaptchaToken] = useState();

  useEffect(() => {
    if (step === steps.receiptDetails) {
      setEmail(emailFromState || '');
      setError({ email: false, phoneNumber: false, reCaptcha: false });
      setRecaptchaToken(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, step]);

  const handleEmailChange = ({ target }) => {
    setError((error) => ({ ...error, email: false }));
    setEmail(target.value);
  };

  const onSubmit = async () => {
    let errorFlag = false;
    if (!isValidEmail(email)) {
      setError((error) => ({ ...error, email: true }));
      errorFlag = true;
    }
    if (!isValidPhoneNumber(fullPhoneNumber)) {
      setError((error) => ({ ...error, phoneNumber: true }));
      errorFlag = true;
    }
    if (!reCaptchaToken) {
      setError((error) => ({ ...error, reCaptcha: true }));
      errorFlag = true;
    }

    if (errorFlag) {
      return;
    }

    dispatch(setSendersEmail(email));
    dispatch(sendSmsToSender({ fullPhoneNumber, reCaptchaToken }));
  };

  const onReCaptchaChangeHandler = (token) => {
    setError((error) => ({ ...error, reCaptcha: false }));
    setRecaptchaToken(token);
  };

  const onEditHandler = () => {
    dispatch(setSingleStep(steps.receiptDetails));
  };

  return (
    <Fragment>
      {step === steps.receiptDetails && <div className="big-title">Almost there!</div>}
      <Card
        step={steps.receiptDetails}
        showStep={false}
        title="Sender details"
        CollapsedContent={CollapsedContent}
        onEdit={onEditHandler}
      >
        <div className="receipt-details">
          <div className="email-wrapper">
            <EmailInput value={email} onChange={handleEmailChange} />
            <div className={[styles.error, error.email && styles.opacity100].join(' ')}>
              Enter a complete email address
            </div>

            <div className="country-and-phone-number-wrapper">
              <div className="country-and-phone-number">
                <div className="country-select-wrapper">
                  <CountrySelect />
                </div>
                <div className="separator" />
                <div className="phone-number-wrapper">
                  <PhoneNumberInput onEnter={() => {}} />
                </div>
              </div>
            </div>
            <div style={{ height: 8 }} />
            <div className={[styles.error, error.phoneNumber && styles.opacity100].join(' ')}>
              Check if the country and phone are correct number. We may not yet support your
              country.
            </div>

            <div className={styles.reCaptcha}>
              <ReCaptcha onChange={onReCaptchaChangeHandler} />
            </div>
            <div style={{ height: 8 }} />
            <div className={[styles.error, error.reCaptcha && styles.opacity100].join(' ')}>
              *Captcha is required!
            </div>
            <div className="notice">Thank you for doing business with us.</div>

            <BaseButton onClick={onSubmit}>Next</BaseButton>
          </div>
        </div>
      </Card>
    </Fragment>
  );
}

function CollapsedContent() {
  const { email, fullPhoneNumber, countryCode } = useSelector((state) => state.sender);
  return (
    <div className="collapsed-content">
      <div className="content country-and-phone-number">{email}</div>
      <div className="content country-and-phone-number">
        <span className="emoji">{getCountryEmojiByCountryCode(countryCode)}</span>
        {fullPhoneNumber}
      </div>
    </div>
  );
}
